<template>
  <SlotPanel>
    <div class="flex justify-between mb-4 px-4">
      <div class="hover:cursor-pointer"><ResArrowLeft id="prev-month" @click="prevMonth" /></div>
      <div class="text-fg-secondary font-sans text-base" id="date-picker-title">
        {{ calendar[0].name }} {{ calendar[0].year }}
      </div>
      <div class="hover:cursor-pointer"><ResArrowRight id="next-month" @click="nextMonth" /></div>
    </div>
    <div class="flex mb-4 gap-x-3 sm:gap-x-4">
      <span
        v-for="(label, weekLabelKey) in weekdayLabels"
        :key="weekLabelKey"
        class="flex-1 text-center font-sans text-fg-secondary"
        >{{ label }}</span
      >
    </div>
    <div>
      <div v-for="(month, monthKey) in calendar" :key="monthKey">
        <div
          v-for="(week, weekKey) in month.weeks"
          :key="weekKey"
          class="flex mb-4 font-sans gap-x-3 sm:gap-x-4"
        >
          <span
            v-for="(day, dayKey) in week.days"
            :key="dayKey"
            class="flex-1 text-center hover:cursor-pointer w-7 h-7 flex items-center justify-center rounded-md"
            :id="'day' + day.date.replaceAll('-', '')"
            @click="onSelectDate(month, day)"
            :class="{
              'text-fg-link-primary font-semibold': !day.isInvalid,
              'text-fg-secondary font-normal': day.isInvalid,
              'border-2 border-current': day.date === today.format('yyyy-MM-DD'),
              'bg-button-primary border-button-primary !text-fg-oncolor selected':
                ui.selectedDate === day.date,
              'bg-transparent text-transparent hover:cursor-default': month.name !== day.monthName,
            }"
          >
            {{ day.day }}
          </span>
        </div>
      </div>
    </div>
  </SlotPanel>
</template>
<script lang="ts">
import moment, { Moment } from 'moment'
import { PropType, defineComponent } from 'vue'

import ResArrowLeft from '@/assets/resArrowLeft.svg?component'
import ResArrowRight from '@/assets/resArrowRight.svg?component'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'

interface CalendarDay {
  date: string
  day: string
  monthName: string
  isInvalid: boolean
}

interface CalendarWeek {
  days: CalendarDay[]
}

interface CalendarMonth {
  name: String
  year: String
  weeks: CalendarWeek[]
}

export default defineComponent({
  name: 'DatePicker',
  components: {
    SlotPanel,
    ResArrowRight,
    ResArrowLeft,
  },
  props: {
    selectedDate: { type: String, required: true },
    preventInvalidDatesClick: { type: Boolean, default: true },
    invalidDates: {
      type: Array as PropType<(string | 'before-today' | 'after-today')[]>,
      default: () => [] as string[],
    },
    validDates: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  data() {
    return {
      today: moment() as Moment,
      selectedMomentDate: moment(this.selectedDate) as Moment,
      weekdayLabels: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      calendar: [] as CalendarMonth[],
      ui: {
        selectedDate: this.selectedDate,
      },
    }
  },
  created() {
    this.generateCalendar()
  },
  methods: {
    generateCalendar() {
      const calendar: CalendarDay[] = []
      const startDay = this.selectedMomentDate.clone().startOf('month').startOf('week')
      const endDay = this.selectedMomentDate.clone().endOf('month').endOf('week')
      const date = startDay.clone().subtract(1, 'day')

      while (date.isBefore(endDay, 'day')) {
        const momentDay = date.add(1, 'day').clone()
        calendar.push({
          date: momentDay.format('yyyy-MM-DD'),
          day: momentDay.format('D'),
          monthName: momentDay.format('MMMM'),
          isInvalid: this.isDateInvalid(momentDay.format('yyyy-MM-DD')),
        })
      }

      const weeks: CalendarWeek[] = calendar.reduce((results: CalendarWeek[], item, index) => {
        const weekIndex = Math.floor(index / 7)

        if (!results[weekIndex]) {
          results[weekIndex] = { days: [] }
        }

        results[weekIndex].days.push(item)

        return results
      }, [])

      this.calendar = []
      this.calendar.push({
        name: startDay.clone().add(10, 'days').format('MMMM'),
        year: startDay.clone().add(10, 'days').format('YYYY'),
        weeks,
      })
    },

    nextMonth() {
      this.selectedMomentDate.add(1, 'month')
      this.generateCalendar()
    },

    prevMonth() {
      this.selectedMomentDate.subtract(1, 'month')
      this.generateCalendar()
    },

    dateSelected(dateObj) {
      this.ui.selectedDate = dateObj.date
      this.$emit('datePickerChange', dateObj)
    },

    onSelectDate(month: CalendarMonth, day: CalendarDay): void {
      if (this.preventInvalidDatesClick && day.isInvalid) return
      if (month.name === day.monthName) this.dateSelected(day)
    },

    isDateInvalid(date: string): boolean {
      if (this.validDates.length > 0) {
        return !this.validDates.includes(date)
      }

      if (this.invalidDates.includes(date)) return true

      if (
        this.invalidDates.includes('before-today') &&
        moment(date).isBefore(this.today.format('yyyy-MM-DD'), 'day')
      ) {
        return true
      }

      return (
        this.invalidDates.includes('after-today') &&
        moment(date).isAfter(this.today.format('yyyy-MM-DD'), 'day')
      )
    },
  },
  watch: {
    invalidDates: {
      handler: 'generateCalendar',
    },
    selectedDate(value: string) {
      this.selectedMomentDate = moment(value)
      this.generateCalendar()
      this.dateSelected({ date: value })
    },
  },
})
</script>
